import { useState, useEffect } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useToast } from "@/hooks/use-toast"
import { useQuery } from "@tanstack/react-query"
import type { EvergreenCatalog, EvergreenCatalogPost } from "@shared/schema"
import { 
  Sparkles,
  Facebook, 
  Instagram, 
  Linkedin, 
  Globe,
  Copy,
  Star,
  TrendingUp,
  Filter,
  Lightbulb,
  BookOpen,
  MessageCircle,
  Image as ImageIcon,
  Calendar,
  FileText,
  ChevronRight
} from "lucide-react"
import { FloatingCard } from "@/components/FloatingCard"
import { AnimatedSection } from "@/components/AnimatedSection"
import { format } from "date-fns"
import { de } from "date-fns/locale"

const platformConfig = {
  facebook: {
    name: "Facebook",
    icon: Facebook,
    color: "bg-blue-600",
    textColor: "text-blue-600"
  },
  instagram: {
    name: "Instagram", 
    icon: Instagram,
    color: "bg-pink-600",
    textColor: "text-pink-600"
  },
  linkedin: {
    name: "LinkedIn",
    icon: Linkedin, 
    color: "bg-blue-700",
    textColor: "text-blue-700"
  },
  google_business: {
    name: "Google My Business",
    icon: Globe,
    color: "bg-green-600", 
    textColor: "text-green-600"
  }
} as const

const contentTypeConfig = {
  tip: { label: "Tipp", icon: Lightbulb, color: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200" },
  "behind-scenes": { label: "Hinter den Kulissen", icon: ImageIcon, color: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200" },
  education: { label: "Bildung", icon: BookOpen, color: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200" },
  story: { label: "Geschichte", icon: MessageCircle, color: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200" }
} as const

const engagementConfig = {
  high: { label: "Hoch", color: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200" },
  medium: { label: "Mittel", color: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200" },
  low: { label: "Niedrig", color: "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200" }
}

interface CatalogsResponse {
  catalogs: EvergreenCatalog[]
  company_id: string
  company_name: string
}

interface CatalogPostsResponse {
  catalog: EvergreenCatalog
  posts: EvergreenCatalogPost[]
  company_id: string
}

export default function EvergreenContent() {
  const [selectedCatalogId, setSelectedCatalogId] = useState<string | null>(null)
  const [selectedContentType, setSelectedContentType] = useState<string>("all")
  const { toast } = useToast()

  // Fetch catalogs for this company
  const { data: catalogsData, isLoading: catalogsLoading, error: catalogsError } = useQuery<CatalogsResponse>({
    queryKey: ['/api/evergreen-catalogs']
  })

  // Fetch posts for selected catalog
  const { data: postsData, isLoading: postsLoading } = useQuery<CatalogPostsResponse>({
    queryKey: ['/api/evergreen-catalogs', selectedCatalogId, 'posts'],
    enabled: !!selectedCatalogId
  })

  const catalogs = catalogsData?.catalogs || []

  // Auto-select latest catalog when data loads
  useEffect(() => {
    if (catalogs.length > 0 && !selectedCatalogId) {
      setSelectedCatalogId(catalogs[0].id)
    }
  }, [catalogs, selectedCatalogId])

  const posts = postsData?.posts || []

  // Filter posts by content type
  const filteredPosts = posts.filter(post => {
    if (selectedContentType === "all") return true
    return post.contentType === selectedContentType
  })

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text)
      toast({
        title: "In Zwischenablage kopiert!",
        description: `${label} wurde erfolgreich kopiert.`
      })
    } catch (error) {
      toast({
        title: "Fehler",
        description: "Konnte nicht in Zwischenablage kopieren.",
        variant: "destructive"
      })
    }
  }

  const renderRelevanceScore = (score: number | null) => {
    if (!score) return null
    const stars = Math.round(score / 2) // Convert 1-10 to 1-5 stars
    return (
      <div className="flex items-center gap-1" data-testid={`relevance-score-${score}`}>
        {Array.from({ length: 5 }).map((_, i) => (
          <Star
            key={i}
            className={`w-4 h-4 ${i < stars ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300 dark:text-gray-600'}`}
          />
        ))}
        <span className="text-xs text-muted-foreground ml-1">({score}/10)</span>
      </div>
    )
  }

  if (catalogsLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center space-y-4">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
            <p className="text-muted-foreground">Lade Evergreen Kataloge...</p>
          </div>
        </div>
      </div>
    )
  }

  if (catalogsError) {
    return (
      <div className="container mx-auto p-6">
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <p className="text-destructive">Fehler beim Laden der Evergreen-Kataloge.</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  // No catalogs yet
  if (catalogs.length === 0) {
    return (
      <div className="container mx-auto p-6 space-y-6">
        <AnimatedSection>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3" data-testid="heading-evergreen-content">
                <Sparkles className="h-8 w-8 text-purple-600" />
                Evergreen Content Katalog
              </h1>
              <p className="text-muted-foreground mt-2">
                Ihr KI-Katalog mit 40 zeitlosen Content-Ideen für Social Media
              </p>
            </div>
          </div>
        </AnimatedSection>

        <AnimatedSection delay={100}>
          <Card>
            <CardContent className="pt-6 text-center py-12">
              <Sparkles className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Noch keine Kataloge vorhanden</h3>
              <p className="text-muted-foreground max-w-md mx-auto">
                Ihr erster Evergreen-Katalog wird automatisch erstellt, 
                sobald Ihr Profil vollständig ist.
              </p>
            </CardContent>
          </Card>
        </AnimatedSection>
      </div>
    )
  }

  const selectedCatalog = catalogs.find(c => c.id === selectedCatalogId)

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <AnimatedSection>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3" data-testid="heading-evergreen-content">
              <Sparkles className="h-8 w-8 text-purple-600" />
              Evergreen Content Katalog
            </h1>
            <p className="text-muted-foreground mt-2">
              Ihr KI-Katalog mit zeitlosen Content-Ideen für Social Media
            </p>
          </div>
          
          {/* Catalog Selector */}
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <Select value={selectedCatalogId || ""} onValueChange={setSelectedCatalogId}>
              <SelectTrigger className="w-[280px]" data-testid="select-catalog">
                <SelectValue placeholder="Katalog wählen" />
              </SelectTrigger>
              <SelectContent>
                {catalogs.map((catalog) => (
                  <SelectItem key={catalog.id} value={catalog.id}>
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      <span>
                        {format(new Date(catalog.createdAt), "d. MMMM yyyy, HH:mm", { locale: de })}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </AnimatedSection>

      {/* Stats */}
      <AnimatedSection delay={100}>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <FloatingCard>
            <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Gesamt Posts</CardTitle>
              <Sparkles className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold" data-testid="stat-total-posts">{posts.length}</div>
              <p className="text-xs text-muted-foreground">Evergreen-Ideen</p>
            </CardContent>
          </FloatingCard>

          <FloatingCard>
            <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Tipps</CardTitle>
              <Lightbulb className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold" data-testid="stat-tips">
                {posts.filter(p => p.contentType === 'tip').length}
              </div>
              <p className="text-xs text-muted-foreground">Praktische Ratschläge</p>
            </CardContent>
          </FloatingCard>

          <FloatingCard>
            <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Bildung</CardTitle>
              <BookOpen className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold" data-testid="stat-education">
                {posts.filter(p => p.contentType === 'education').length}
              </div>
              <p className="text-xs text-muted-foreground">Lernressourcen</p>
            </CardContent>
          </FloatingCard>

          <FloatingCard>
            <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Hohe Relevanz</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold" data-testid="stat-high-relevance">
                {posts.filter(p => (p.relevanceScore ?? 0) >= 8).length}
              </div>
              <p className="text-xs text-muted-foreground">Score ≥ 8/10</p>
            </CardContent>
          </FloatingCard>
        </div>
      </AnimatedSection>

      {/* Filters */}
      <AnimatedSection delay={200}>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Filter
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <label className="text-sm font-medium mb-2 block">Content-Typ</label>
                <Select value={selectedContentType} onValueChange={setSelectedContentType}>
                  <SelectTrigger data-testid="select-content-type">
                    <SelectValue placeholder="Content-Typ wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle Typen</SelectItem>
                    <SelectItem value="tip">Tipps</SelectItem>
                    <SelectItem value="behind-scenes">Hinter den Kulissen</SelectItem>
                    <SelectItem value="education">Bildung</SelectItem>
                    <SelectItem value="story">Geschichten</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button 
                  variant="outline" 
                  onClick={() => setSelectedContentType("all")}
                  data-testid="button-clear-filters"
                >
                  Filter zurücksetzen
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </AnimatedSection>

      {/* Posts Grid */}
      <AnimatedSection delay={300}>
        {postsLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center space-y-4">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
              <p className="text-muted-foreground">Lade Posts...</p>
            </div>
          </div>
        ) : filteredPosts.length === 0 ? (
          <Card>
            <CardContent className="pt-6 text-center">
              <p className="text-muted-foreground">Keine Evergreen-Posts gefunden.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {filteredPosts.map((post) => {
              const contentTypeInfo = contentTypeConfig[post.contentType as keyof typeof contentTypeConfig]
              const ContentTypeIcon = contentTypeInfo?.icon || Sparkles
              const platforms = Array.isArray(post.platforms) ? post.platforms : []
              const engagement = post.estimatedEngagement as keyof typeof engagementConfig | null

              return (
                <FloatingCard key={post.id} className="hover-elevate">
                  <CardHeader>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <Badge variant="outline" className="text-xs" data-testid={`badge-post-number-${post.id}`}>
                            #{post.number}
                          </Badge>
                          {contentTypeInfo && (
                            <Badge className={contentTypeInfo.color} data-testid={`badge-content-type-${post.id}`}>
                              <ContentTypeIcon className="w-3 h-3 mr-1" />
                              {contentTypeInfo.label}
                            </Badge>
                          )}
                        </div>
                        <CardTitle className="text-lg" data-testid={`post-title-${post.id}`}>
                          {post.topic}
                        </CardTitle>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => copyToClipboard(
                          `Thema: ${post.topic}\nWinkel: ${post.angle}\nGrund: ${post.reason}`,
                          "Post-Details"
                        )}
                        data-testid={`button-copy-${post.id}`}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Angle */}
                    <div>
                      <h4 className="text-sm font-semibold mb-1">Winkel:</h4>
                      <p className="text-sm text-muted-foreground" data-testid={`post-angle-${post.id}`}>{post.angle}</p>
                    </div>

                    {/* Reason */}
                    {post.reason && (
                      <div>
                        <h4 className="text-sm font-semibold mb-1">Begründung:</h4>
                        <p className="text-sm text-muted-foreground" data-testid={`post-reason-${post.id}`}>{post.reason}</p>
                      </div>
                    )}

                    {/* Hook Preview */}
                    {post.hookPreview && (
                      <div>
                        <h4 className="text-sm font-semibold mb-1">Hook:</h4>
                        <p className="text-sm text-muted-foreground italic" data-testid={`post-hook-${post.id}`}>
                          "{post.hookPreview}"
                        </p>
                      </div>
                    )}

                    {/* Platforms */}
                    {platforms.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold mb-2">Plattformen:</h4>
                        <div className="flex flex-wrap gap-2">
                          {platforms.map((platform: string) => {
                            const config = platformConfig[platform as keyof typeof platformConfig]
                            if (!config) return null
                            const Icon = config.icon
                            return (
                              <Badge
                                key={platform}
                                variant="outline"
                                className={`${config.textColor} border-current`}
                                data-testid={`badge-platform-${platform}-${post.id}`}
                              >
                                <Icon className="w-3 h-3 mr-1" />
                                {config.name}
                              </Badge>
                            )
                          })}
                        </div>
                      </div>
                    )}

                    {/* Metrics */}
                    <div className="flex items-center justify-between pt-2 border-t flex-wrap gap-2">
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Relevanz</p>
                        {renderRelevanceScore(post.relevanceScore)}
                      </div>
                      {engagement && engagementConfig[engagement] && (
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Engagement</p>
                          <Badge className={engagementConfig[engagement].color} data-testid={`badge-engagement-${post.id}`}>
                            <TrendingUp className="w-3 h-3 mr-1" />
                            {engagementConfig[engagement].label}
                          </Badge>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </FloatingCard>
              )
            })}
          </div>
        )}
      </AnimatedSection>

      {/* Catalog Info Footer */}
      {selectedCatalog && (
        <AnimatedSection delay={400}>
          <Card className="bg-muted/50">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center justify-between flex-wrap gap-2 text-sm text-muted-foreground">
                <div className="flex items-center gap-4">
                  <span>Katalog erstellt: {format(new Date(selectedCatalog.createdAt), "d. MMMM yyyy, HH:mm", { locale: de })}</span>
                  {selectedCatalog.processingTime && (
                    <span>Generierungszeit: {Number(selectedCatalog.processingTime).toFixed(1)}s</span>
                  )}
                </div>
                <div>
                  Kataloge insgesamt: {catalogs.length}
                </div>
              </div>
            </CardContent>
          </Card>
        </AnimatedSection>
      )}
    </div>
  )
}
